import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import ServicePage from '../../components/site/ServicePage';
import SiteShowcase from '../../components/site/SiteShowcase';
import Sources from '../../components/site/Sources';
import { WEBSITES_FAQS as FAQS } from '../../site/faqs';
import { CONTACT } from '../../site/nav';
import { SOURCES } from '../../site/sources';
import { WORK_GROUPS, workIn } from '../../site/work';

const Cite = ({ id }) => (
  <a href={SOURCES[id].href} target="_blank" rel="noopener" className="text-blue-600 hover:text-slate-900 underline underline-offset-4 decoration-blue-400/40">{SOURCES[id].name}</a>
);

// The Websites page: the sites we built, shown moving, then one section per
// kind of business saying what that site is built around. The 75% figure
// is Drizzled's own result and is cited like every other number on the
// site (src/site/sources.js). The sections and the sites read
// src/site/work.js.
export default function Websites() {
  return (
    <ServicePage
      path="/websites"
      eyebrow="Websites"
      title="A website built around what makes you money."
      lead="Orders for a restaurant. Bookings for a clinic. Walk-ins for a service office. We build the site around that one thing. Drizzled in Stockton saw 75% more online orders after theirs."
      proof={{ label: 'See the sites', href: '#work' }}
      features={[
        { title: 'Fast on a phone', text: 'Most of your visitors are on a phone, in a hurry. The site opens at once and the button they need is on the screen.' },
        { title: 'Found on Google', text: 'Every page is real text that Google and AI assistants can read, so a search for what you sell and your city lands on you.' },
        { title: 'Facts in one place', text: 'Hours, address, prices, and the menu or services live in one place and update everywhere.' },
        { title: 'English and Spanish', text: 'Every page in both languages. The visitor picks once and the site remembers.' },
        { title: 'Your Google reviews', text: 'Five-star reviews on the page, with a button to leave one.' },
        { title: 'Your domain, your links', text: 'The site goes on your domain. Old addresses redirect, so nothing you shared stops working.' },
      ]}
      customer={{
        title: 'What your customers get',
        items: [
          { title: 'The answer in one tap', text: 'Order, book, call, or get directions from the first screen.' },
          { title: 'Hours they can trust', text: 'A live open or closed line, so nobody shows up to a closed door.' },
          { title: 'A reason to come back', text: 'If you want it, a spin to win game with prize codes your staff check at the counter.' },
        ],
      }}
      steps={[
        { title: 'Send us what you have', text: 'Your menu or services, photos, prices, hours, and your ordering or booking link.' },
        { title: 'We put it on your domain', text: 'Old addresses redirect, and Google is told about every page the day it goes live.' },
        { title: 'You watch the numbers', text: 'Orders, bookings, or calls. Changes to prices or hours are one message to us.' },
      ]}
      faqs={FAQS}
      closing={{ title: 'See one on your phone first.', text: `Open ${CONTACT.restaurantSite.replace('https://', '').replace('/', '')}, tap the menu, then book 30 minutes.` }}
      after={<Sources ids={['S9']} />}
    >
      <ResultBand />
      <SiteShowcase />
      {WORK_GROUPS.map((g, i) => <GroupSection key={g.id} group={g} alt={i % 2 === 1} />)}
    </ServicePage>
  );
}

// The one number, with where it comes from.
function ResultBand() {
  return (
    <section className="pb-12 md:pb-20 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 md:p-10 grid md:grid-cols-[auto_1fr] gap-5 md:gap-10 items-center">
          <p className="text-6xl md:text-7xl font-bold tracking-tight text-blue-600 leading-none">75%</p>
          <div>
            <p className="text-slate-900 text-lg md:text-xl font-bold tracking-tight mb-1.5">more online orders at Drizzled after the new site</p>
            <p className="text-slate-500 text-sm md:text-base leading-relaxed">
              A dessert trailer in North Stockton. Same menu, same trailer, same ordering. The difference is a site people can find and order from on a phone. Measured in <Cite id="S9" />.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// One kind of business: what the site is built around, three points, and
// the live sites of that kind as links.
function GroupSection({ group, alt }) {
  const sites = workIn(group.id);
  return (
    <section id={group.id} className={`py-12 md:py-20 px-4 border-t border-slate-200 scroll-mt-24 ${alt ? 'bg-slate-50' : ''}`}>
      <div className="container mx-auto max-w-6xl grid lg:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
        <div>
          <p className="text-blue-600 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-3">{group.name}</p>
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight mb-3 md:mb-4">{group.around}</h2>
          <p className="text-slate-500 md:text-lg leading-relaxed">{group.text}</p>
          {group.price && (
            <p className="mt-4 flex items-baseline gap-2">
              <span className="text-3xl md:text-4xl font-bold text-slate-900">{group.price}</span>
              <span className="text-slate-400 text-sm font-semibold">/mo</span>
              <span className="text-slate-500 text-xs md:text-sm">{group.setup}</span>
            </p>
          )}
          {group.price && group.includes && (
            <p className="mt-2 text-slate-700 text-sm font-semibold">{group.includes[0]}</p>
          )}
          {group.price === null && group.setup && (
            <p className="mt-4 text-slate-500 text-sm">{group.setup}</p>
          )}
          {group.more && (
            <Link to={group.more.path} className="mt-4 inline-flex items-center gap-1.5 text-blue-600 hover:text-slate-900 font-semibold text-sm">
              {group.more.label}
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          <div className="flex flex-wrap gap-2 mt-6">
            {sites.map((w) => (
              <a key={w.slug} href={w.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:border-blue-500 hover:text-slate-900 transition-colors">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: w.accent }} aria-hidden="true" />
                {w.name}
                <ArrowUpRight className="w-3.5 h-3.5 text-blue-600" />
              </a>
            ))}
          </div>
        </div>
        <ul className="grid sm:grid-cols-3 lg:grid-cols-1 gap-3 md:gap-4">
          {group.points.map((p) => (
            <li key={p.title} className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
              <h3 className="text-slate-900 font-bold tracking-tight mb-1">{p.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{p.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
